import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Mail, Rocket, Users, TrendingUp, Star, ExternalLink, ArrowRight, CheckCircle, Loader2, Globe, Heart } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

const benefits = [
    {
        icon: Users,
        title: "Reach New Audiences",
        description: "Get your brand in front of thousands of fashion-forward shoppers actively looking for new styles."
    },
    {
        icon: TrendingUp,
        title: "Boost Visibility",
        description: "Featured placement in our app"
    },
    {
        icon: Sparkles,
        title: "AI-Powered Matching",
        description: "Our AI matches your products with users whose style preferences align with your brand aesthetic."
    },
    {
        icon: Star,
        title: "Build Credibility",
        description: "Being featured on fashionDisc helps establish your brand as a trusted fashion destination."
    }
];

export default function Promoters() {
    const [promoters, setPromoters] = useState([]);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchPromoters = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/promoters`);
            setPromoters(res.data.promoters || []);
        } catch { setPromoters([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPromoters(); }, [fetchPromoters]);

    return (
        <div className="min-h-screen bg-[#faf9f7]">
            {/* Hero Section */}
            <section className="relative overflow-hidden py-20 lg:py-32">
                <div className="absolute inset-0 bg-[#faf9f7]" />
                <div className="absolute top-20 right-10 w-72 h-72 bg-stone-200/20 rounded-full blur-3xl" />
                <div className="absolute bottom-10 left-10 w-96 h-96 bg-stone-200/20 rounded-full blur-3xl" />
                
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-6">Partner With Us</p>
                        
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extralight text-stone-800 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                            Showcase Your Brand to the 
                            <em className="font-light italic text-stone-600">World</em>
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-stone-600 mb-8 leading-relaxed">
                            Are you a new designer, emerging brand, or startup clothing company? 
                            Partner with fashionDisc to get your products in front of thousands of style-conscious shoppers 
                            looking for the next big thing in fashion.
                        </p>
                        
                        <motion.a
                            href="mailto:fashiondisc25@gmail.com?subject=Brand Collaboration Inquiry"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="inline-flex items-center gap-3 px-8 py-4 bg-stone-800 hover:bg-stone-900 text-white text-xs uppercase tracking-[0.15em] font-medium rounded-full transition-all"
                        >
                            <Mail className="w-5 h-5" />
                            Get in Touch for Collaboration
                            <ArrowRight className="w-5 h-5" />
                        </motion.a>
                        
                        <p className="mt-4 text-sm text-stone-500">
                            Email us at <span className="font-medium text-terracotta-600">fashiondisc25@gmail.com</span>
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Influencer Collaboration Section */}
            <section className="py-12 lg:py-16 bg-[#faf9f7]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-br from-stone-50 to-orange-50/30 rounded-3xl p-6 lg:p-8 border border-stone-200/50 text-center"
                    >
                        <div className="flex justify-center mb-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-suede-500 to-terracotta-500 flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        <h3 className="text-xl sm:text-2xl font-light text-stone-800 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                            Open to Influencer Collaborations
                        </h3>
                        <p className="text-stone-500 max-w-lg mx-auto mb-5 text-sm leading-relaxed">
                            Are you a fashion influencer or content creator? We'd love to work with you! 
                            Get early access to features, co-create content, and grow with fashionDisc.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
                            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-xs text-stone-600 border border-stone-200">
                                <Heart className="w-3 h-3 text-terracotta-500" /> Early Access
                            </span>
                            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-xs text-stone-600 border border-stone-200">
                                <Star className="w-3 h-3 text-amber-500" /> Exclusive Features
                            </span>
                            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-xs text-stone-600 border border-stone-200">
                                <Users className="w-3 h-3 text-suede-500" /> Community Spotlight
                            </span>
                        </div>
                        <a
                            href="mailto:fashiondisc25@gmail.com?subject=Influencer Collaboration Inquiry"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-suede-500 to-terracotta-500 text-white text-xs uppercase tracking-[0.15em] font-medium rounded-full hover:shadow-lg hover:shadow-suede-500/20 transition-all"
                        >
                            <Mail className="w-4 h-4" />
                            Let's Collaborate
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-16 lg:py-24 bg-[#faf9f7]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-3">Benefits</p>
                        <h2 className="text-3xl sm:text-4xl font-extralight text-stone-800 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                            Why Partner with fashionDisc?
                        </h2>
                        <p className="text-base text-stone-500 max-w-2xl mx-auto">
                            We're passionate about helping emerging brands grow. Here's what you get when you collaborate with us.
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="p-6 bg-white/60 rounded-2xl hover:bg-white transition-colors group border border-stone-100"
                            >
                                <div className="w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <benefit.icon className="w-6 h-6 text-stone-600" />
                                </div>
                                <h3 className="text-lg font-light text-stone-800 mb-2" style={{ fontFamily: 'Georgia, serif' }}>{benefit.title}</h3>
                                <p className="text-stone-600 text-sm leading-relaxed">{benefit.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-16 lg:py-24 bg-[#faf9f7]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-3">Process</p>
                        <h2 className="text-3xl sm:text-4xl font-extralight text-stone-800 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                            How It Works
                        </h2>
                        <p className="text-base text-stone-500 max-w-2xl mx-auto">
                            Getting your brand featured is simple. Follow these steps to start your journey with us.
                        </p>
                    </motion.div>

                    <div className="max-w-3xl mx-auto">
                        {[
                            { step: 1, title: "Reach Out", description: "Send us an email telling us about your brand, your story, and what makes your products special." },
                            { step: 2, title: "We Review", description: "Our team reviews your submission to ensure it aligns with our quality standards and community values." },
                            { step: 3, title: "Get Featured", description: "Once approved, your brand and products will be showcased in our Picks of the Week promoting section for all users to discover." },
                            { step: 4, title: "Grow Together", description: "Track your brand's performance and grow your customer base through our platform." },
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="flex gap-6 mb-8 last:mb-0"
                            >
                                <div className="flex-shrink-0 w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center text-stone-600 font-light text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                                    {item.step}
                                </div>
                                <div className="pt-2">
                                    <h3 className="text-xl font-light text-stone-800 mb-1" style={{ fontFamily: 'Georgia, serif' }}>{item.title}</h3>
                                    <p className="text-stone-600">{item.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Brands Section */}
            <section className="py-16 lg:py-24 bg-white/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-3">Collaborations</p>
                        <h2 className="text-3xl sm:text-4xl font-extralight text-stone-800 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                            Discover Emerging Designers
                        </h2>
                        <p className="text-base text-stone-500 max-w-2xl mx-auto">
                            Shop from talented new brands and designers handpicked by our team.
                        </p>
                    </motion.div>

                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
                        </div>
                    ) : promoters.length > 0 ? (
                        <div className="space-y-12">
                            {promoters.map((brand, index) => {
                                const products = Array.isArray(brand.featured_products) ? brand.featured_products : [];
                                return (
                                <motion.div
                                    key={brand.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-stone-50 rounded-3xl p-6 lg:p-8"
                                >
                                    <div className="flex items-center gap-4 mb-6">
                                        {brand.logo_url ? (
                                            <img 
                                                src={brand.logo_url} 
                                                alt={brand.brand_name}
                                                className="w-24 h-24 rounded-xl object-cover"
                                            />
                                        ) : (
                                            <div className="w-24 h-24 rounded-full border border-stone-300 flex items-center justify-center text-stone-600 font-light text-3xl" style={{ fontFamily: 'Georgia, serif' }}>
                                                {brand.brand_name?.[0] || '?'}
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <h3 className="text-xl font-light text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>{brand.brand_name}</h3>
                                            <p className="text-stone-600">{brand.description}</p>
                                        </div>
                                        {brand.website_url && (
                                            <a 
                                                href={brand.website_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-xl hover:bg-stone-800 transition-colors"
                                            >
                                                <Globe className="w-4 h-4" />
                                                Visit Store
                                                <ExternalLink className="w-4 h-4" />
                                            </a>
                                        )}
                                    </div>
                                    
                                    {products.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {products.map((product, pi) => (
                                            <div 
                                                key={product.id || pi}
                                                className="bg-white rounded-2xl overflow-hidden group cursor-pointer"
                                            >
                                                {product.image && (
                                                <div className="aspect-[3/4] overflow-hidden">
                                                    <img 
                                                        src={product.image}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                    />
                                                </div>
                                                )}
                                                <div className="p-3">
                                                    <p className="font-medium text-stone-900 truncate">{product.name}</p>
                                                    {product.price && <p className="text-terracotta-600 font-semibold">{product.price}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    )}

                                    {brand.website_url && (
                                        <a 
                                            href={brand.website_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="sm:hidden flex items-center justify-center gap-2 mt-4 px-4 py-3 bg-stone-900 text-white rounded-xl hover:bg-stone-800 transition-colors"
                                        >
                                            Visit Store
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    )}
                                </motion.div>
                                );
                            })}
                        </div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center py-16 bg-stone-50 rounded-3xl"
                        >
                            <div className="w-20 h-20 rounded-full border border-stone-300 flex items-center justify-center mx-auto mb-6">
                                <Rocket className="w-10 h-10 text-stone-400" />
                            </div>
                            <h3 className="text-2xl font-light text-stone-800 mb-3" style={{ fontFamily: 'Georgia, serif' }}>Be the First!</h3>
                            <p className="text-stone-600 max-w-md mx-auto mb-6">
                                We're looking for amazing emerging brands to feature here. 
                                Could yours be the first?
                            </p>
                            <a
                                href="mailto:fashiondisc25@gmail.com?subject=Brand Collaboration Inquiry"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-stone-800 hover:bg-stone-900 text-white text-xs uppercase tracking-[0.15em] font-medium rounded-full transition-all"
                            >
                                <Mail className="w-5 h-5" />
                                Apply to Be Featured
                            </a>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 lg:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-3xl p-8 lg:p-12 text-center relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-terracotta-500/20 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-suede-500/20 rounded-full blur-3xl" />
                        
                        <div className="relative">
                            <h2 className="text-3xl sm:text-4xl font-extralight text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                                Ready to Launch Your Brand?
                            </h2>
                            <p className="text-lg text-stone-300 mb-8 max-w-2xl mx-auto">
                                Join the fashionDisc community and let us help you reach your target audience. 
                                We're excited to hear your story and showcase your creations.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <a
                                    href="mailto:fashiondisc25@gmail.com?subject=Brand Collaboration Inquiry"
                                    className="inline-flex items-center gap-3 px-8 py-4 bg-white text-stone-900 text-xs uppercase tracking-[0.15em] font-medium rounded-full hover:bg-stone-100 transition-all"
                                >
                                    <Mail className="w-5 h-5" />
                                    Email Us Now
                                </a>
                            </div>
                            
                            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-stone-400 text-sm">
                                <span className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-olive-400" />
                                    Free to Apply
                                </span>
                                <span className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-olive-400" />
                                    Quick Response
                                </span>
                                <span className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-olive-400" />
                                    No Commitment
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
